import { motion } from "framer-motion";
import { Terminal, Github, Linkedin } from "lucide-react";
import { useRouter } from "../context/RouterContext";
import { useEffect, useState } from "react";
import { useLocale } from "../context/LocaleContext";
import api from "../services/api";

interface FooterData {
  site_title: string;
  site_description: string;
  quick_links_title: string;
  quick_link_home: string;
  quick_link_articles: string;
  resources_title: string;
  resources_link_resources: string;
  resources_link_projects: string;
  resources_link_about: string;
  connect_title: string;
  copyright_text: string;
  github_url: string;
  linkedin_url: string;
}

export function Footer() {
  const { navigate } = useRouter();
  const { locale } = useLocale();
  const [siteName, setSiteName] = useState({ pt1: "DevOps", pt2: "❤ Cagla" });
  const [footerData, setFooterData] = useState<FooterData>({
    site_title: "DevOps by Cagla",
    site_description:
      "Your comprehensive resource for DevOps practices, tools, and methodologies.",
    quick_links_title: "Quick Links",
    quick_link_home: "Home",
    quick_link_articles: "Articles",
    resources_title: "Resources",
    resources_link_resources: "Resources",
    resources_link_projects: "Projects",
    resources_link_about: "About",
    connect_title: "Connect",
    copyright_text: "All rights reserved.",
    github_url: "https://github.com/caglayagmuricerr",
    linkedin_url: "https://www.linkedin.com/in/cagla-yagmur-icer-206a88248/",
  });

  useEffect(() => {
    async function fetchFooterData() {
      try {
        const response: any = await api.getGlobal(locale);

        // Fetch site name
        if (response?.data?.site_name) {
          const siteNameData = response.data.site_name;
          setSiteName({
            pt1: siteNameData.pt1 || "DevOps",
            pt2: siteNameData.pt2 || "❤ Cagla",
          });
        }

        // Fetch footer data
        if (response?.data?.footer) {
          setFooterData(response.data.footer);
        }
      } catch (error) {
        console.error("Failed to fetch footer data:", error);
      }
    }

    fetchFooterData();
  }, [locale]);

  return (
    <footer className="bg-black border-t border-red-900/20 mt-auto">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Terminal className="w-6 h-6 text-red-600" />
              <span className="text-lg font-bold text-white">
                {siteName.pt1}
                <span className="text-red-600"> {siteName.pt2}</span>
              </span>
            </div>
            <p className="text-gray-400 text-sm">
              {footerData.site_description}
            </p>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-4">
              {footerData.quick_links_title}
            </h3>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => navigate("home")}
                  className="text-gray-400 hover:text-red-600 transition-colors text-sm"
                >
                  {footerData.quick_link_home}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate("articles")}
                  className="text-gray-400 hover:text-red-600 transition-colors text-sm"
                >
                  {footerData.quick_link_articles}
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-4">
              {footerData.resources_title}
            </h3>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => navigate("resources")}
                  className="text-gray-400 hover:text-red-600 transition-colors text-sm"
                >
                  {footerData.resources_link_resources}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate("projects")}
                  className="text-gray-400 hover:text-red-600 transition-colors text-sm"
                >
                  {footerData.resources_link_projects}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate("about")}
                  className="text-gray-400 hover:text-red-600 transition-colors text-sm"
                >
                  {footerData.resources_link_about}
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-4">
              {footerData.connect_title}
            </h3>
            <div className="flex gap-4">
              {[
                { Icon: Github, href: footerData.github_url },
                { Icon: Linkedin, href: footerData.linkedin_url },
              ].map(({ Icon, href }, idx) => (
                <motion.a
                  key={idx}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="text-gray-400 hover:text-red-600 transition-colors"
                >
                  <Icon className="w-5 h-5" />
                </motion.a>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-red-900/20 mt-8 pt-8 text-center">
          <p className="text-gray-400 text-sm">
            &copy; {new Date().getFullYear()} {footerData.site_title}.{" "}
            {footerData.copyright_text}
          </p>
        </div>
      </div>
    </footer>
  );
}
